import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, switchMap} from 'rxjs';
import {catchError, map, shareReplay, startWith, take} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {DEFAULT_OFFSET} from '@gnv-utils';
import {StationsRepo} from 'src/app/repositories/station-repo';
import {ServiceType, StationHealthState} from 'src/swagger-gen/web-secured/models';
import {StationGnvConnectivityPagedList} from 'src/swagger-gen/gnvert-secured/models';
import {RouteIndicatorItem} from 'src/app/shared/types/routes/indicator-route.types';
import {BadgeViewSeverity} from 'src/app/shared/types/common/badge-view.types';
import {
  DEFAULT_LOADING_VALUE,
  DEFAULT_MIN_VALUE,
  formatTabIndicatorValue
} from 'src/app/shared/types/tab-indicator/tab-indicator';

const COUNT_ONLY_PAGE_SIZE = 1;

const WARNING_MAX_COUNT = 5;

const TAB_BADGE_CLASS_BY_SEVERITY: Record<BadgeViewSeverity, string> = {
  success: 'green-bg',
  warning: 'orange-bg',
  error: 'red-bg',
  primary: '',
  neutral: '',
};

export function getDisconnectedStationsSeverity(count: number): BadgeViewSeverity {
  if (count <= 0) {
    return 'success';
  }

  return count <= WARNING_MAX_COUNT ? 'warning' : 'error';
}

@Injectable({
  providedIn: 'root',
})
export class StationConnectivityCountRepo {

  private readonly stationRepo = inject(StationsRepo);
  private readonly translate = inject(TranslateService);

  private startIndicator: RouteIndicatorItem = {
    value: DEFAULT_LOADING_VALUE,
    tooltip: this.translate.instant('tabs-indicators.common.loading-tooltip')
  };

  private refreshDisconnectedStations$ = new BehaviorSubject<void>(undefined);

  public disconnectedStationsCount$ = this.refreshDisconnectedStations$.pipe(
    switchMap(() => this.loadDisconnectedStationsCount()),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  private loadDisconnectedStationsCount(): Observable<RouteIndicatorItem> {
    return this.stationRepo.getAllStationsConnectivity(
      DEFAULT_OFFSET,
      COUNT_ONLY_PAGE_SIZE,
      [ServiceType.IN_USE, ServiceType.IN_USE_WITH_RESTRICTIONS],
      StationHealthState.BAD
    ).pipe(
      take(1),
      map((res: StationGnvConnectivityPagedList) => {
        const count = res?.paging?.totalItems ?? 0;

        return ({
          value: formatTabIndicatorValue(count),
          tooltip: this.translate.instant('tabs-indicators.stationConnectivity.disconnectedStations.tooltip', { count }),
          styleClass: this.getStyleClass(count),
        });
      }),
      startWith(this.startIndicator),
      catchError(() => of({ value: DEFAULT_MIN_VALUE }))
    );
  }

  private getStyleClass(count: number): string {
    return TAB_BADGE_CLASS_BY_SEVERITY[getDisconnectedStationsSeverity(count)];
  }

  public refreshDisconnectedStations(): void {
    this.refreshDisconnectedStations$.next();
  }
}
